// Analytics utility functions for tracking app usage

export const ANALYTICS_STORAGE_KEY = 'knipper-genai-hub-analytics';

// Initialize analytics data structure
export const initializeAnalytics = () => {
  const existingData = localStorage.getItem(ANALYTICS_STORAGE_KEY);
  if (!existingData) {
    const initialData = {
      totalClicks: 0,
      appClicks: {},
      categoryClicks: {},
      dailyStats: {},
      clickHistory: []
    };
    localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(initialData));
    return initialData;
  }
  return JSON.parse(existingData);
};

// Record an app click
export const recordAppClick = (app) => {
  const analyticsData = initializeAnalytics();
  const timestamp = new Date();
  const dateKey = timestamp.toISOString().split('T')[0]; // YYYY-MM-DD format
  
  // Update total clicks
  analyticsData.totalClicks += 1;
  
  // Update app-specific clicks
  if (!analyticsData.appClicks[app.name]) {
    analyticsData.appClicks[app.name] = 0;
  }
  analyticsData.appClicks[app.name] += 1;
  
  // Update category clicks
  const categoryName = app.category.name;
  if (!analyticsData.categoryClicks[categoryName]) {
    analyticsData.categoryClicks[categoryName] = 0;
  }
  analyticsData.categoryClicks[categoryName] += 1;
  
  // Update daily stats
  if (!analyticsData.dailyStats[dateKey]) {
    analyticsData.dailyStats[dateKey] = 0;
  }
  analyticsData.dailyStats[dateKey] += 1;
  
  // Add to click history (keep last 1000 clicks)
  analyticsData.clickHistory.push({
    appName: app.name,
    category: categoryName,
    timestamp: timestamp.toISOString(),
    url: app.url
  });
  
  // Keep only last 1000 clicks to prevent storage bloat
  if (analyticsData.clickHistory.length > 1000) {
    analyticsData.clickHistory = analyticsData.clickHistory.slice(-1000);
  }
  
  // Save to localStorage
  localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(analyticsData));
  
  return analyticsData;
};

// Get analytics data
export const getAnalyticsData = () => {
  return initializeAnalytics();
};

// Get analytics for a specific date range
export const getAnalyticsForDateRange = (startDate, endDate) => {
  const analyticsData = getAnalyticsData();
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const filteredHistory = analyticsData.clickHistory.filter(click => {
    const clickDate = new Date(click.timestamp);
    return clickDate >= start && clickDate <= end;
  });
  
  // Calculate stats for the filtered data
  const appClicks = {};
  const categoryClicks = {};
  const dailyStats = {};
  
  filteredHistory.forEach(click => {
    const dateKey = click.timestamp.split('T')[0];
    
    // App clicks
    if (!appClicks[click.appName]) {
      appClicks[click.appName] = 0;
    }
    appClicks[click.appName] += 1;
    
    // Category clicks
    if (!categoryClicks[click.category]) {
      categoryClicks[click.category] = 0;
    }
    categoryClicks[click.category] += 1;
    
    // Daily stats
    if (!dailyStats[dateKey]) {
      dailyStats[dateKey] = 0;
    }
    dailyStats[dateKey] += 1;
  });
  
  return {
    totalClicks: filteredHistory.length,
    appClicks,
    categoryClicks,
    dailyStats,
    clickHistory: filteredHistory
  };
};

// Get top apps
export const getTopApps = (limit = 10) => {
  const analyticsData = getAnalyticsData();
  return Object.entries(analyticsData.appClicks)
    .sort(([,a], [,b]) => b - a)
    .slice(0, limit)
    .map(([name, clicks]) => ({ name, clicks }));
};

// Get daily usage for last N days
export const getDailyUsageForLastDays = (days = 30) => {
  const analyticsData = getAnalyticsData();
  const today = new Date();
  const dailyUsage = [];
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateKey = date.toISOString().split('T')[0];
    
    dailyUsage.push({
      date: dateKey,
      clicks: analyticsData.dailyStats[dateKey] || 0,
      formattedDate: date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      })
    });
  }
  
  return dailyUsage;
};

// Clear analytics data (for testing/reset)
export const clearAnalyticsData = () => {
  localStorage.removeItem(ANALYTICS_STORAGE_KEY);
  return initializeAnalytics();
};